import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';
import '@resources/css/main.scss';
import 'dayjs/locale/vi';

import { ConfigProvider, ThemeConfig } from 'antd';
import dayjs from 'dayjs';
import { NextPage } from 'next';
import { DefaultSeo, OrganizationJsonLd, SiteLinksSearchBoxJsonLd } from 'next-seo';
import { SEO } from 'next-seo.config';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Inter } from 'next/font/google';
import Head from 'next/head';
import React, { ReactElement, ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import store, { persistor } from '@store/index';

dayjs.locale('vi');

const ToastContainer = dynamic(() => import('react-toastify').then((mod) => mod.ToastContainer), {
  ssr: false,
});

const ModalController = dynamic(
  () => import('@components/shared/modal').then((component) => component.ModalController),
  {
    ssr: false,
  }
);

const theme: ThemeConfig = {
  token: {
    fontSize: 16,
    colorPrimary: '#ea580c',
    colorSuccess: '#38C976',
    colorWarning: '#FCB121',
    colorError: '#FD504F',
  },
  components: {
    Button: {
      colorPrimary: '#ea580c',
      colorBgContainerDisabled: '#6b7280',
      colorTextDisabled: '#ffffff',
      opacityLoading: 1,
    },
    Input: {
      colorPrimary: '#ea580c',
    },
    Menu: {
      itemHeight: 48,
      itemColor: '#34306a',
      itemHoverColor: '#34306a',
      itemHoverBg: '#ffffff',
    },
    Segmented: {
      itemSelectedBg: '#ea580c',
      itemHoverBg: '#e5e7eb',
      itemSelectedColor: '#ffffff',
      itemHoverColor: '#ea580c',
      trackBg: '#f5f5f5',
      itemColor: '#ea580c',
      itemActiveBg: '#e5e7eb',
    },
  },
};

type Props = AppProps & {
  Component: NextPage & {
    getLayout?: (page: ReactElement) => ReactNode;
  };
};
const inter = Inter({ subsets: ['latin'], variable: '--font-inter' });

function App({ Component, pageProps }: Props) {
  const getLayout = Component.getLayout || ((page) => page);

  return (
    <React.Fragment>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
      </Head>
      <DefaultSeo {...SEO} />
      <OrganizationJsonLd
        type="Organization"
        name="Thực phẩm Quảng Đà"
        url="https://thucphamquangda.com"
        logo="https://thucphamquangda.com/img/logo.png"
        contactPoint={[
          {
            telephone: '+84 123 456 789',
            contactType: 'customer service',
            areaServed: 'VN',
            availableLanguage: 'Vietnamese',
          },
        ]}
        sameAs={[
          'https://www.facebook.com/thucphamquangda',
          // 'https://www.instagram.com/thucphamquangda',
        ]}
      />
      <SiteLinksSearchBoxJsonLd
        url="https://thucphamquangda.com"
        potentialActions={[
          {
            target: 'https://thucphamquangda.com/san-pham?search={search_term_string}',
            queryInput: 'required name=search_term_string',
          },
        ]}
      />
      <main className={inter.variable}>
        <Provider store={store}>
          <PersistGate persistor={persistor}>
            <ConfigProvider theme={theme}>
              <ToastContainer />
              <ModalController />
              {getLayout(<Component {...pageProps} />)}
            </ConfigProvider>
          </PersistGate>
        </Provider>
      </main>
    </React.Fragment>
  );
}

export default App;
